import React, { useState } from 'react';
import { Form } from 'react-form';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { CardElement, useStripe, useElements } from '@stripe/react-stripe-js';

import requireAuth from '../hocs/requireAuth';
import { required } from '../../utils/validation';
import { paymentProviderTypeIds } from '../../utils/eventConstants';

import { Input, Select } from '../Form';
import CheckoutFormStripeError from './CheckoutFormStripeError';

const CheckoutFormStripe = (props) => {
    const {
        payButtonLabel,
        onCheckoutProcessStart,
        onPaymentMethodCreateFailed,
    } = props;

    const coreUI = useSelector(({ coreUI }) => coreUI);

    const [cardTouched, setCardTouched] = useState(false);
    const [cardComplete, setCardComplete] = useState(false);
    const [cardError, setCardError] = useState(null);
    const [processingPayment, setProcessingPayment] = useState(false);

    const stripe = useStripe();
    const elements = useElements();

    const handleSubmitPaymentMethod = async (billingDetails) => {
        // bail out quietly if these are not loaded yet
        if (!stripe || !elements) { return; }

        if (!cardTouched || !cardComplete) {
            // inject a fake error
            setCardError({
                type: 'UtrError',
                code: null,
                message: 'Card details are incomplete.' });
            return;
        }
        setProcessingPayment(true);
        const cardElement = elements.getElement(CardElement);

        const { error, paymentMethod } = await stripe.createPaymentMethod({
            type: 'card',
            card: cardElement,
            billing_details: billingDetails,
        });
        if (error) {
            // resetting "touched" to false prevents immediate resubmission
            // after a failed attempt
            setCardTouched(false);
            setCardError(error);
            onPaymentMethodCreateFailed(error);
        } else {
            // parent component can now bounce this checkoutContext to the API
            onCheckoutProcessStart({
                paymentProviderId: paymentProviderTypeIds.STRIPE,
                paymentMethodId: paymentMethod.id,
            });
        }
        setProcessingPayment(false);
    };

    const validateCardholderName = (value) => {
        return !required(value) ? '' : 'You must provide a name.';
    };

    const validateCardholderCountry = (value) => {
        return !required(value) ? '' : 'Your country is incomplete.';
    };

    return (
        <Form onSubmit={handleSubmitPaymentMethod}>
            {(formApi) => {
                const { submitForm } = formApi;
                return (
                    <form onSubmit={submitForm} id="CheckoutFormStripe" data-testid="checkoutformstripe.form-80">
                        <div>
                            <div className={`row`}>
                                <Input
                                    id="cardholder-name"
                                    field="name"
                                    label="Cardholder Name"
                                    formGroupCssClass="col-12"
                                    placeholder="ex. Serena Williams"
                                    disableProfanityCheck={true}
                                    validate={validateCardholderName}
                                    data-testid="event-reg.cardholder-name-input"
                                />
                            </div>
                            <div className="row">
                                <div className={`form-group form-group-xl col-12 ${cardError && 'is-invalid'}`}>
                                    <label data-testid="checkoutformstripe.label-97">Card Details</label>
                                    <div className="input-wrapper">
                                        <CardElement
                                            options={{
                                                style: {
                                                    base: {
                                                        fontSize: '16px',
                                                        color: '#424770',
                                                        '::placeholder': {
                                                            color: '#aab7c4',
                                                        },
                                                    },
                                                    invalid: {
                                                        color: '#9e2146',
                                                    },
                                                },
                                            }}
                                            className="form-control mb16"
                                            onChange={(e) => {
                                                setCardTouched(true);
                                                setCardComplete(!!(e?.complete));
                                                setCardError(e?.error);
                                            }}
                                        />
                                        <div className="error-indicator" />
                                    </div>
                                    {cardError && <CheckoutFormStripeError error={cardError} />}
                                </div>
                            </div>
                            <div className="row">
                                <Select
                                    id="cardholder-country"
                                    field="address.country"
                                    label="Billing Country"
                                    formGroupCssClass="col-12"
                                    hiddenDefault={true}
                                    attributeToSave="value"
                                    options={coreUI.types.wePayCountriesList}
                                    validate={validateCardholderCountry}
                                        data-testid="event-reg.cardholder-country-select"
                                />
                            </div>
                            <div className="row">
                                <button
                                    type="submit"
                                    className={`btn btn-xl btn-primary text-uppercase fw600 w-100 ${processingPayment ? 'text-dark' : ''}`}
                                    disabled={!stripe || !elements || !cardTouched || !cardComplete || processingPayment}
                                >
                                    {processingPayment && (<i className="fas fa-circle-notch fa-spin" />)} {payButtonLabel}
                                </button>
                            </div>
                        </div>
                    </form>
                );
            }}
        </Form>
    );
};

CheckoutFormStripe.propTypes = {
    payButtonLabel: PropTypes.string,
    onCheckoutProcessStart: PropTypes.func.isRequired,
    onPaymentMethodCreateFailed: PropTypes.func.isRequired,
};

CheckoutFormStripe.defaultProps = {
    payButtonLabel: 'Pay',
};

export default requireAuth(CheckoutFormStripe);
